import { useEffect, useRef } from "react";
import satsangData from "~/data/appsInfo/satsangapp.json";
import guSatsangData from "~/data/appsInfo/satsangapp_gu.json";
import { useFontStyle } from "~/hook/useFontStyle";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { onPauseVideo } from "~/lib/store/slice/youtubePlayerSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { FALLBACK_IMAGE_ALT_TEXT } from "~/utils/constant";

function SatsangAppInfo() {
  const lang = useLanguageParams();
  const { appInfo, description, features } = lang === "gu" ? guSatsangData : satsangData;
  const { normalInter, medium } = useFontStyle();
  const { isVideoPlay } = useAppSelector((state) => state.youtubeVideo);
  const videoRef = useRef<HTMLVideoElement>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handlePlay = () => {
      dispatch(onPauseVideo());
    };

    videoElement.addEventListener("play", handlePlay);
    return () => {
      videoElement.removeEventListener("play", handlePlay);
    };
  }, [dispatch]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;
    if (isVideoPlay) {
      videoElement.pause();
    }
  }, [isVideoPlay]);

  return (
    <div className="w-full max-md:px-3 md:w-1/2 flex flex-col gap-5 text-textSecondaryColor font-inter">
      {/* Introduction */}
      <p className={cn("body_text_m text-textSecondaryColor", normalInter)}> {description.introduction}</p>

      {/* App Video */}
      <div className="w-full aspect-video flex justify-center items-center rounded-lg overflow-hidden bg-black">
        <video
          ref={videoRef}
          controls={true}
          width="100%"
          height="auto"
          playsInline
          webkit-playsinline="true"
          preload="metadata"
          aria-label="Satsang App demonstration video"
          className="w-full h-full bg-black "
          poster={appInfo.icon}
          onError={(e) => {}}
        >
          <source src={appInfo.video} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>

      {/* Features Introduction */}
      <p className={cn("text-textColor body_text_l", normalInter)}>{description.featuresIntro}</p>

      {/* Features List */}
      <div className="space-y-3">
        {features.map((feature, index) => (
          <div key={index} className="border-l-4 border-borderPrimary pl-4">
            <h4 className={cn("font-semibold text-textColor mb-1 body_text_l", medium)}>{feature.title}</h4>
            <p className={cn("body_text_m text-textSecondaryColor", normalInter)}>{feature.description}</p>
          </div>
        ))}
      </div>

      {/* App Download Section */}
      <div className="w-full flex flex-col gap-4 items-center mb-5">
        <img
          src={appInfo.icon}
          title={appInfo.name || FALLBACK_IMAGE_ALT_TEXT}
          alt={appInfo.name || FALLBACK_IMAGE_ALT_TEXT}
          className="max-w-full h-auto rounded-3xl select-none"
        />
        <span className={cn("text-center font-medium body_text_m", normalInter)}>{appInfo.downloadText}</span>
        <div className="flex gap-4">
          <a href={appInfo.downloadLinks.playStore.url} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
            <img
              src={appInfo.downloadLinks.playStore.image}
              title={appInfo.downloadLinks.playStore.alt || FALLBACK_IMAGE_ALT_TEXT}
              alt={appInfo.downloadLinks.playStore.alt || FALLBACK_IMAGE_ALT_TEXT}
              className="select-none"
            />
          </a>
          <a href={appInfo.downloadLinks.appStore.url} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
            <img
              src={appInfo.downloadLinks.appStore.image}
              title={appInfo.downloadLinks.appStore.alt || FALLBACK_IMAGE_ALT_TEXT}
              alt={appInfo.downloadLinks.appStore.alt || FALLBACK_IMAGE_ALT_TEXT}
              className="select-none"
            />
          </a>
        </div>
      </div>
    </div>
  );
}

export default SatsangAppInfo;
